import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";

import { useToast } from "hooks/toast";
import {
  getEmbarcador,
  importarPlanilha,
  baixarPlanilhaModelo,
  enviarKitSponsor,
} from "services/onboardingApi";
import { downloadBlob } from "utils/downloadBlob";

import OnboardingLista from "./components/OnboardingLista";
import ChecklistHome from "./components/ChecklistHome";
import IdentidadeForm from "./components/IdentidadeForm";
import FiliaisStep from "./components/FiliaisStep";
import UsuariosStep from "./components/UsuariosStep";
import ProdutoStep from "./components/ProdutoStep";
import TransportadorasStep from "./components/TransportadorasStep";
import ConfigAutomaticaStep from "./components/ConfigAutomaticaStep";
import HomologacaoStep from "./components/HomologacaoStep";
import "./onboarding.css";

const STORAGE_KEY = "@HubEverlog:onboardingEmbarcadorId";

const STEP_TITLES = {
  produto: {
    titulo: "Produto / Plano contratado",
    sub: "Os módulos que o cliente contratou. Definido pelo Projeto no fechamento, gateia tudo que vem depois.",
  },
  identidade: {
    titulo: "Identidade do Embarcador (Matriz)",
    sub: "Informe o CNPJ. Os dados vêm da BrasilAPI e ficam editáveis.",
  },
  filiais: {
    titulo: "Filiais / Plantas (Remetentes)",
    sub: "As plantas que embarcam pela Everlog. Adicione por CNPJ.",
  },
  usuarios: {
    titulo: "Usuários do Embarcador",
    sub: "Quem vai acessar o Hub deste embarcador?",
  },
  transportadoras: {
    titulo: "Transportadoras",
    sub: "Fonte: aba \"Lista CNPJ Transportador\" da planilha de onboarding. Cadastro/vínculo reusa o auto-cadastro (CNPJ + BrasilAPI).",
  },
  configuracao: {
    titulo: "Configuração automática",
    sub: "O sistema pré-preenche a maioria com defaults; itens \"A confirmar\" pedem uma decisão do Projeto antes de seguir.",
  },
  homologacao: {
    titulo: "Homologação",
    sub: "Conferência final do Projeto antes de o embarcador ficar operacional.",
  },
};

// Onboarding de Embarcador (PO1-2428, épico PO1-2427) — wizard do Projeto.
// Fluxo: lista (OnboardingLista, PO1-2897: em andamento / concluídos / todos)
// → checklist retomável → passos. O estado dos passos é derivado do dado no
// backend; o embarcador em edição fica na URL (?id=, link compartilhável) e
// no localStorage (fallback) para retomar direto ao voltar.
export default function OnboardingEmbarcador() {
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  const [view, setView] = useState("lista");
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [baixandoModelo, setBaixandoModelo] = useState(false);
  // PO1-2746: modal "Enviar kit ao sponsor"
  const [modalKitAberto, setModalKitAberto] = useState(false);
  const [sponsorNome, setSponsorNome] = useState("");
  const [sponsorEmail, setSponsorEmail] = useState("");
  const [enviandoKit, setEnviandoKit] = useState(false);
  const [usuariosPlanilha, setUsuariosPlanilha] = useState([]);
  const inputPlanilhaRef = useRef(null);

  // PO1-2897: o embarcador aberto fica em ?id= (link compartilhável, F5
  // reabre o mesmo). replace, não push: voltar do navegador sai da tela em
  // vez de percorrer cada onboarding aberto.
  const setIdNaUrl = useCallback(
    (id) => {
      const search = id ? `?id=${id}` : "";
      if (history.location.search !== search) {
        history.replace({ pathname: history.location.pathname, search });
      }
    },
    [history]
  );

  const abrirEmbarcador = useCallback(
    async (id, proximaView = "checklist") => {
      setCarregando(true);
      try {
        const data = await getEmbarcador(id);
        setDados(data);
        localStorage.setItem(STORAGE_KEY, String(id));
        setIdNaUrl(id);
        setView(proximaView);
      } catch (err) {
        console.error("[Onboarding] getEmbarcador:", err);
        localStorage.removeItem(STORAGE_KEY);
        setIdNaUrl(null);
        setView("lista");
        addToast({
          place: "tr",
          type: "danger",
          title: "Não foi possível abrir o onboarding",
          description:
            (err.response && err.response.data && err.response.data.error) ||
            err.message,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setCarregando(false);
      }
    },
    [setIdNaUrl, addToast]
  );

  // Ao montar: ?id= na URL tem precedência; sem ele, o último id salvo no
  // localStorage (comportamento antigo). Roda uma vez só.
  useEffect(() => {
    const idUrl = Number(new URLSearchParams(location.search).get("id"));
    if (Number.isInteger(idUrl) && idUrl > 0) {
      abrirEmbarcador(idUrl);
      return;
    }
    const salvo = localStorage.getItem(STORAGE_KEY);
    if (salvo) {
      abrirEmbarcador(Number(salvo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recarregar = useCallback(() => {
    if (dados && dados.embarcador) {
      abrirEmbarcador(dados.embarcador.id, view);
    }
  }, [dados, view, abrirEmbarcador]);

  const voltarPraLista = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setIdNaUrl(null);
    setDados(null);
    setView("lista");
  }, [setIdNaUrl]);

  const voltarProChecklist = useCallback(() => {
    if (dados && dados.embarcador) {
      abrirEmbarcador(dados.embarcador.id, "checklist");
    } else {
      voltarPraLista();
    }
  }, [dados, abrirEmbarcador, voltarPraLista]);

  const abrirPasso = useCallback((passo) => {
    if (STEP_TITLES[passo]) {
      setView(passo);
    }
  }, []);

  const handlePlanilha = useCallback(
    async (event) => {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || !dados) return;
      setImportando(true);
      try {
        const resultado = await importarPlanilha(dados.embarcador.id, file);
        const usuarios = resultado.usuarios || [];
        const usuariosNovos = usuarios.filter((u) => !u.status || u.status === "novo");
        const usuariosJaTratados = usuarios.length - usuariosNovos.length;
        setUsuariosPlanilha(usuariosNovos);
        const partes = [
          `${resultado.filiais.criadas.length} filial(is)`,
          `${resultado.transportadoras.criadas.length} transportadora(s)`,
          `${usuariosNovos.length} usuário(s) para revisar no passo Usuários` +
            (usuariosJaTratados
              ? ` (${usuariosJaTratados} já convidado(s)/existente(s), fora da lista)`
              : ""),
        ];
        const puladas =
          resultado.filiais.puladas.length +
          resultado.transportadoras.puladas.length;
        // PO1-3284: cada transportadora da planilha entra na esteira; o retorno
        // traz `estado` por item (e `puladas` com motivo "Curadoria da Everlog").
        const estados = (resultado.transportadoras.itens || resultado.transportadoras.criadas || [])
          .map((t) => t.estado)
          .filter(Boolean);
        const emCuradoria = resultado.transportadoras.puladas.filter((t) => t.estado === "EM_CURADORIA").length;
        const ESTADO_LABEL = { AGUARDANDO_HOMOLOGACAO: "aguardando homologação", EM_CONFIGURACAO: "aguardando homologação", EM_ONBOARDING: "em onboarding", OPERACIONAL: "operacional", DEVOLVIDO: "devolvida" };
        const resumoEstados = [...new Set(estados)]
          .map((e) => `${estados.filter((x) => x === e).length} ${ESTADO_LABEL[e] || e}`)
          .join(", ");
        if (resumoEstados) partes.push(`esteira: ${resumoEstados}`);
        if (emCuradoria) partes.push(`${emCuradoria} na Curadoria da Everlog`);
        addToast({
          place: "tr",
          type: emCuradoria ? "warning" : "success",
          title: "Planilha importada",
          description:
            partes.join(" · ") + (puladas ? ` · ${puladas} pulada(s)` : ""),
          icon: "tim-icons icon-cloud-upload-94",
        });
        await abrirEmbarcador(dados.embarcador.id, "checklist");
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          err.message;
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao importar planilha",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setImportando(false);
      }
    },
    [dados, addToast, abrirEmbarcador]
  );

  // PO1-2660: baixa o template único versionado no backend_hub, pelo helper
  // compartilhado de download de blob autenticado (utils/downloadBlob).
  const handleBaixarModelo = useCallback(async () => {
    setBaixandoModelo(true);
    try {
      const blob = await baixarPlanilhaModelo();
      downloadBlob(blob, "Onboard_Embarcador.xlsx");
    } catch (err) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao baixar planilha padrão",
        description: err.message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setBaixandoModelo(false);
    }
  }, [addToast]);

  // PO1-2746: dispara o e-mail ao sponsor com a planilha anexada e o link
  // de upload tokenizado.
  const handleEnviarKitSponsor = useCallback(async () => {
    if (!dados) return;
    if (!sponsorNome.trim() || !sponsorEmail.trim()) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Informe nome e e-mail do sponsor",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    setEnviandoKit(true);
    try {
      await enviarKitSponsor(dados.embarcador.id, { sponsorNome, sponsorEmail });
      addToast({
        place: "tr",
        type: "success",
        title: "Kit enviado ao sponsor",
        icon: "tim-icons icon-check-2",
      });
      setModalKitAberto(false);
      setSponsorNome("");
      setSponsorEmail("");
      await abrirEmbarcador(dados.embarcador.id, "checklist");
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao enviar kit ao sponsor",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setEnviandoKit(false);
    }
  }, [dados, sponsorNome, sponsorEmail, addToast, abrirEmbarcador]);

  // A lista vive em components/OnboardingLista.jsx (PO1-2897) e é renderizada
  // em largura total, fora do .onb-wrap do checklist.
  const renderPasso = () => {
    const meta = STEP_TITLES[view];
    return (
      <>
        <Button color="link" size="sm" onClick={voltarProChecklist}>
          ◂ Voltar ao checklist
        </Button>
        <h4 style={{ marginBottom: 2 }}>{meta.titulo}</h4>
        <p style={{ fontSize: 12, color: "#9a9a9a" }}>{meta.sub}</p>
        {view === "produto" && (
          <ProdutoStep
            idEmbarcador={dados.embarcador.id}
            onSaved={voltarProChecklist}
          />
        )}
        {view === "identidade" && (
          <IdentidadeForm
            mode="edit"
            embarcador={dados.embarcador}
            onSaved={() => {
              abrirEmbarcador(dados.embarcador.id, "checklist");
            }}
            onCancel={voltarProChecklist}
          />
        )}
        {view === "filiais" && (
          <FiliaisStep
            idEmbarcador={dados.embarcador.id}
            filiais={dados.filiais}
            onChanged={recarregar}
          />
        )}
        {view === "usuarios" && (
          <UsuariosStep
            idEmbarcador={dados.embarcador.id}
            linhasIniciais={usuariosPlanilha}
            onChanged={recarregar}
          />
        )}
        {view === "transportadoras" && (
          <TransportadorasStep
            idEmbarcador={dados.embarcador.id}
            onChanged={recarregar}
          />
        )}
        {view === "configuracao" && (
          <ConfigAutomaticaStep
            idEmbarcador={dados.embarcador.id}
            onChanged={recarregar}
            operacional={dados.checklist.statusEmbarcador === "OPERACIONAL"}
          />
        )}
        {view === "homologacao" && (
          <HomologacaoStep
            dados={dados}
            onHomologado={() => {
              abrirEmbarcador(dados.embarcador.id, "homologacao");
            }}
          />
        )}
        {view !== "identidade" && view !== "produto" && (
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={voltarProChecklist}>
              Voltar
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="content">
      {view === "lista" && (
        <OnboardingLista
          onAbrir={(id) => abrirEmbarcador(id)}
          onNovo={() => setView("novo")}
        />
      )}
      {view !== "lista" && (
        <div className="onb-wrap">
          <Card>
            <CardHeader />
            <CardBody>
              {view === "novo" && (
                <>
                  <Button color="link" size="sm" onClick={voltarPraLista}>
                    ◂ Voltar
                  </Button>
                  <h4 style={{ marginBottom: 2 }}>Novo embarcador</h4>
                  <p style={{ fontSize: 12, color: "#9a9a9a" }}>
                    O embarcador é criado como <b>PENDENTE</b> e só vira
                    operacional na homologação.
                  </p>
                  <IdentidadeForm
                    mode="create"
                    onSaved={(resultado) => {
                      abrirEmbarcador(resultado.embarcador.id);
                    }}
                    onCancel={voltarPraLista}
                  />
                </>
              )}
              {view === "checklist" && dados && (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <Button color="link" size="sm" onClick={voltarPraLista}>
                      ◂ Todos os onboardings
                    </Button>
                    <div>
                      <Button
                        color="success"
                        size="sm"
                        outline
                        className="mr-2"
                        onClick={() => setModalKitAberto(true)}
                      >
                        Enviar kit ao sponsor
                      </Button>
                      <Button
                        color="info"
                        size="sm"
                        outline
                        className="mr-2"
                        disabled={baixandoModelo}
                        onClick={handleBaixarModelo}
                      >
                        {baixandoModelo ? (
                          <Spinner size="sm" />
                        ) : (
                          "Baixar planilha padrão"
                        )}
                      </Button>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        ref={inputPlanilhaRef}
                        style={{ display: "none" }}
                        onChange={handlePlanilha}
                      />
                      <Button
                        color="info"
                        size="sm"
                        outline
                        disabled={importando}
                        onClick={() =>
                          inputPlanilhaRef.current && inputPlanilhaRef.current.click()
                        }
                      >
                        {importando ? (
                          <Spinner size="sm" />
                        ) : (
                          "Importar planilha padrão"
                        )}
                      </Button>
                    </div>
                  </div>
                  <ChecklistHome dados={dados} onOpenStep={abrirPasso} />
                </>
              )}
              {STEP_TITLES[view] && dados && renderPasso()}
              {carregando && (
                <div className="text-center mt-2">
                  <Spinner size="sm" />
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* modal-align-top = superfície do tema (como todos os outros modais do
          Hub). Sem isso o .modal-content cai no branco do Bootstrap e o texto
          dos inputs, que segue $input-color do tema escuro, fica branco no
          branco — o nome do sponsor só aparecia ao selecionar. */}
      <Modal
        isOpen={modalKitAberto}
        toggle={() => setModalKitAberto(false)}
        modalClassName="modal-align-top"
        fade={false}
      >
        <ModalHeader toggle={() => setModalKitAberto(false)}>
          Enviar kit ao sponsor
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "var(--everlog-card-muted)" }}>
            Envia a planilha padrão de onboarding e um link de upload sem
            login para o sponsor indicado pelo comercial. Reenviar invalida
            o link anterior.
          </p>
          <FormGroup>
            <Label>Nome do sponsor</Label>
            <Input
              value={sponsorNome}
              onChange={(e) => setSponsorNome(e.target.value)}
              placeholder="Nome completo"
            />
          </FormGroup>
          <FormGroup>
            <Label>E-mail do sponsor</Label>
            <Input
              type="email"
              value={sponsorEmail}
              onChange={(e) => setSponsorEmail(e.target.value)}
              placeholder="sponsor@cliente.com.br"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalKitAberto(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleEnviarKitSponsor}
            disabled={enviandoKit}
          >
            {enviandoKit ? <Spinner size="sm" /> : "Enviar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
